import { useCallback, useRef, useState } from 'react'

import {
  Button,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap'

import validate from 'validate.js'

// Extend Window interface for totem global
declare global {
  interface Window {
    totem?: {
      onContactFormSubmit?: () => void
      validateRecaptcha?: (
        callback: (additionalParams?: Record<string, string>) => void,
      ) => void
    }
  }
}

const availableFields = [
  'name',
  'email',
  'phone',
  'associationName',
  'associationWebsite',
  'associationSize',
  'comments',
] as const

type AvailableField = (typeof availableFields)[number]

interface FieldValue {
  field: AvailableField
  isValid: boolean
  value: string
}

interface ContactFormProps {
  title?: string
  fields?: readonly AvailableField[]
  requiredFields?: readonly AvailableField[]
}

interface FieldProps {
  id: string
  type: 'text' | 'email' | 'tel' | 'textarea' | 'select' | 'url'
  placeholder?: string | boolean
  required?: boolean
  onChange?: (
    evt: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => void
  onBlur?: (
    evt: React.FocusEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => void
  isInvalid?: boolean
  invalidMsg?: string
  labelText?: string | boolean
  containerProps?: React.HTMLAttributes<HTMLDivElement>
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>
  inputProps?: React.InputHTMLAttributes<HTMLInputElement> &
    React.TextareaHTMLAttributes<HTMLTextAreaElement> &
    React.SelectHTMLAttributes<HTMLSelectElement>
  children?: React.ReactNode
}

const Field = ({
  id,
  type,
  placeholder,
  required,
  onChange,
  onBlur,
  isInvalid,
  invalidMsg,
  labelText,
  containerProps,
  labelProps,
  inputProps,
  children,
}: FieldProps) => {
  const defaultInputProps = {
    name: id,
    placeholder: placeholder ? placeholder : '',
    invalid: isInvalid,
    valid: false,
    id,
    type,
    onChange,
    onBlur,
    required,
  }
  const fieldLabelProps = Object.assign({}, labelProps, {
    for: id,
    className: labelProps.className + ` ${isInvalid ? 'text-danger' : ''}`,
  })
  const fieldInputProps = Object.assign({}, inputProps, defaultInputProps)
  return (
    <FormGroup {...containerProps}>
      {labelText && <Label {...fieldLabelProps}>{labelText}</Label>}
      {children ? (
        <Input {...fieldInputProps}>{children}</Input>
      ) : (
        <Input {...fieldInputProps} />
      )}
      <FormFeedback className="pt-1">{invalidMsg}</FormFeedback>
    </FormGroup>
  )
}

Field.defaultProps = {
  placeholder: false,
  labelText: false,
  children: false,
  required: false,
  isInvalid: false,
  invalidMsg: 'This field is required',
  containerProps: { className: 'mb-4' },
  labelProps: { className: 'font-weight-bold' },
  inputProps: {},
}

export function ContactForm({
  title = 'Get in touch with TOTEM',
  fields = availableFields,
  requiredFields = availableFields,
}: ContactFormProps) {
  const [submitted, setSubmitted] = useState<boolean>(false)
  const [fieldValues, setFieldValues] = useState<FieldValue[]>(
    [...fields].map((f) => ({
      field: f,
      isValid: true,
      value: '',
    })),
  )
  const formRef = useRef<HTMLFormElement>(null)

  const getFormField = useCallback(
    (field) => {
      return [...fieldValues].filter((f) => f.field === field)[0]
    },
    [fieldValues],
  )

  const validateField = useCallback(
    (field) => {
      setFieldValues((prev) => [
        ...prev.filter((f) => f.field !== field),
        Object.assign({}, getFormField(field), { isValid: true }),
      ])
    },
    [getFormField],
  )

  const invalidateField = useCallback(
    (field) => {
      setFieldValues((prev) => [
        ...prev.filter((f) => f.field !== field),
        Object.assign({}, getFormField(field), { isValid: false }),
      ])
    },
    [getFormField],
  )

  // TODO extract validator function
  const handleBlur = useCallback(
    (field, evt) => {
      const inputValue = evt.target.value
      switch (field) {
        case 'email': {
          const isEmailValid = validate.single(inputValue, {
            presence: true,
            email: true,
          })
          if (isEmailValid === undefined) {
            validateField(field)
          } else {
            invalidateField(field)
          }
          break
        }
        default:
          if (inputValue.length === 0) {
            invalidateField(field)
          } else {
            validateField(field)
          }
          break
      }
    },
    [validateField, invalidateField],
  )

  const handleChange = useCallback(
    (field, evt) => {
      const value = evt.target.value
      setFieldValues((prev) => [
        ...prev.filter((f) => f.field !== field),
        Object.assign({}, getFormField(field), { value }),
      ])
    },
    [getFormField],
  )

  const handleSubmit = useCallback((e) => {
    console.log('handleSubmit()')

    const form = formRef.current
    const url = form.action
    // if (form.checkValidity()) {
    e.preventDefault()
    // let formData = $(form).serialize(); //JSON();
    const formData = new FormData(form)

    if (formData.get('honeypot')) {
      console.error('hidden honeypot has value. preventing form submit')
      return
    }

    function postFormData(additionalParams = {}) {
      Object.keys(additionalParams).forEach((key) =>
        formData.append(key, additionalParams[key]),
      )

      // Convert FormData to URLSearchParams
      const urlParams = new URLSearchParams()
      for (const [key, value] of (formData as any).entries()) {
        urlParams.append(key, value.toString())
      }

      const requestBody = {
        method: 'post',
        body: urlParams.toString(),
        headers: new Headers({
          'Content-Type': 'application/x-www-form-urlencoded',
          // 'Content-Type': 'multipart/form-data',
          // 'Content-Type': 'application/json',
          Accept: 'application/json',
        }),
      }
      fetch(url, requestBody)
        .then(function (response) {
          return response.json()
        })
        .then((data) => {
          if (data.status == '422') {
            throw new Error(data.errors)
          } else {
            setSubmitted(true)
            // location.href = 'signup-submitted';
            if (
              window.totem &&
              window.totem.onContactFormSubmit &&
              typeof window.totem.onContactFormSubmit === 'function'
            ) {
              window.totem.onContactFormSubmit()
            }
          }
        })
        .catch(function (errorDic) {
          console.log(errorDic)
          // var errList = $('#registration-errors');
          // errList.empty();
          // Object.keys(errorDic).forEach(key => {
          //   errorDic[key].forEach(err => {
          //     if (key == 'name') {
          //       var li = $('<li/>')
          //         .text('company' + ' ' + err)
          //         .appendTo(errList);
          //     } else {
          //       var li = $('<li/>')
          //         .text(key + ' ' + err)
          //         .appendTo(errList);
          //     }
          //   });
          // });
        })
    }

    if (
      window.totem &&
      window.totem.validateRecaptcha &&
      typeof window.totem.validateRecaptcha === 'function'
    )
      window.totem.validateRecaptcha(postFormData)
    else postFormData()

    // }
  }, [])

  const renderSuccess = () => {
    return (
      <Row className="justify-content-center">
        Thank you for your inquiry. We will contact you shortly.
      </Row>
    )
  }

  const formIsValid = !fieldValues.map((f) => f.isValid).includes(false)
  const invalidFields = [...fieldValues]
    .filter((f) => !f.isValid)
    .map((f) => f.field)
  const utmFields = [
    'utm_source',
    'utm_campaign',
    'utm_medium',
    'utm_term',
    'utm_content',
    'referrer',
    'landing_page',
  ]

  return (
    <Container>
      <Row className="mb-5">
        <h3 className="h4 w-100 text-center font-weight-bold">{title}</h3>
      </Row>
      {submitted ? (
        renderSuccess()
      ) : (
        <Row className="justify-content-center">
          <Form
            className="col-12"
            style={{ padding: 0 }}
            action="/form"
            method="post"
            onSubmit={handleSubmit}
            innerRef={formRef}
          >
            <input
              id="honeypot"
              name="honeypot"
              type="text"
              style={{ display: 'none' }}
            />
            <input name="channel" value="contact" type="hidden" />

            {utmFields.map((utm) => (
              <input
                key={utm}
                name={utm}
                placeholder={utm}
                defaultValue={sessionStorage.getItem(utm)}
                readOnly
                type="hidden"
              />
            ))}

            {fields.includes('name') && (
              <Field
                id="contactFullName"
                type="text"
                placeholder="Full Name"
                labelText="Full Name"
                onChange={(evt) => handleChange('name', evt)}
                onBlur={(evt) =>
                  requiredFields.includes('name') && handleBlur('name', evt)
                }
                isInvalid={invalidFields.includes('name')}
                required={requiredFields.includes('name')}
              />
            )}
            {fields.includes('email') && (
              <Field
                id="contactEmail"
                type="email"
                placeholder="email@yourprovider.com"
                labelText="Email"
                onChange={(evt) => handleChange('email', evt)}
                onBlur={(evt) =>
                  requiredFields.includes('email') && handleBlur('email', evt)
                }
                isInvalid={invalidFields.includes('email')}
                required={requiredFields.includes('email')}
              />
            )}
            {fields.includes('phone') && (
              <Field
                id="contactTel"
                type="tel"
                placeholder="(123) 456-7890"
                labelText="Phone Number"
                onChange={(evt) => handleChange('phone', evt)}
                onBlur={(evt) =>
                  requiredFields.includes('phone') && handleBlur('phone', evt)
                }
                isInvalid={invalidFields.includes('phone')}
                invalidMsg="Please enter a valid phone number"
                required={requiredFields.includes('phone')}
              />
            )}
            {fields.includes('associationName') && (
              <Field
                id="contactAssociationName"
                type="text"
                placeholder="Association Name"
                labelText="Association Name"
                onChange={(evt) => handleChange('associationName', evt)}
                onBlur={(evt) =>
                  requiredFields.includes('associationName') &&
                  handleBlur('associationName', evt)
                }
                isInvalid={invalidFields.includes('associationName')}
                required={requiredFields.includes('associationName')}
              />
            )}
            {fields.includes('associationWebsite') && (
              <Field
                id="contactAssociationWebsite"
                type="url"
                placeholder="association-website.com"
                labelText="Association Website"
                onChange={(evt) => handleChange('associationWebsite', evt)}
                onBlur={(evt) =>
                  requiredFields.includes('associationWebsite') &&
                  handleBlur('associationWebsite', evt)
                }
                isInvalid={invalidFields.includes('associationWebsite')}
                required={requiredFields.includes('associationWebsite')}
              />
            )}
            {fields.includes('associationSize') && (
              <Field
                id="contactAssociationSize"
                type="select"
                labelText="How many members does your association have?"
                onChange={(evt) => handleChange('associationSize', evt)}
                onBlur={(evt) =>
                  requiredFields.includes('associationSize') &&
                  handleBlur('associationSize', evt)
                }
              >
                {[
                  '10 - 100',
                  '100 - 1,000',
                  '1,000 - 10,000',
                  '10,000 - 100,000',
                  '100,000 +',
                ].map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </Field>
            )}
            {fields.includes('comments') && (
              <Field
                id="contactComments"
                type="textarea"
                inputProps={{ rows: 4 }}
                labelText="Comments or Questions"
                onChange={(evt) => handleChange('comments', evt)}
                onBlur={(evt) =>
                  requiredFields.includes('comments') &&
                  handleBlur('comments', evt)
                }
                isInvalid={invalidFields.includes('comments')}
                required={requiredFields.includes('comments')}
              />
            )}
            <Button size="lg" color="primary" disabled={!formIsValid}>
              Send
            </Button>
          </Form>
        </Row>
      )}
    </Container>
  )
}
